import { isEligibleCityListing } from "@/utils/cityArea";
import { getModelPage, matchesModelPage } from "@/data/modelPages";
import { isKnownFuelSlug, matchesFuel } from "@/utils/fuelGroups";
import { slugify } from "@/utils/listingSlug";

/** Known Romanian cities, so /cars/{make}/{city} filters by location. */
export const KNOWN_CITIES: Record<string, string> = {
  bucuresti: "București", bucharest: "București", cluj: "Cluj-Napoca", "cluj-napoca": "Cluj-Napoca",
  timisoara: "Timișoara", iasi: "Iași", constanta: "Constanța", brasov: "Brașov", craiova: "Craiova",
  oradea: "Oradea", sibiu: "Sibiu", ploiesti: "Ploiești", galati: "Galați", arad: "Arad", pitesti: "Pitești",
  "baia-mare": "Baia Mare", buzau: "Buzău", "satu-mare": "Satu Mare", botosani: "Botoșani", suceava: "Suceava",
  "targu-mures": "Târgu Mureș", bacau: "Bacău", selimbar: "Selimbar", medias: "Mediaș", vaslui: "Vaslui",
  focsani: "Focșani", tulcea: "Tulcea", deva: "Deva", hunedoara: "Hunedoara", "alba-iulia": "Alba Iulia",
  alexandria: "Alexandria",
};

export const normalizeCity = (text: string): string =>
  text.toLowerCase().replace(/ă/g, "a").replace(/â/g, "a").replace(/î/g, "i").replace(/ș/g, "s").replace(/ț/g, "t").replace(/\s+/g, "-");

export const detectCity = (model?: string) => (model ? KNOWN_CITIES[normalizeCity(model)] || null : null);

/** fuelCode = main listings_v2.fuel_type column (the filter source); fuel = display text only. */
export type MakeModelCar = { id: string; make?: string; model?: string; fuel?: string; fuelCode?: string | null; location: string };
export type MakeModelParams = { make?: string; model?: string; fuel?: string };

const normModel = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]/g, "");

/**
 * Explicit model inclusions per brand (normalized): the page model also shows these
 * variants' listings. One-way unless listed both ways. No substring matching.
 */
export const MODEL_INCLUSIONS: Record<string, Record<string, string[]>> = {
  bmw: { seria5: ["seria5e61"] },
  audi: { q5: ["sq5"] },
  dacia: { logan: ["loganmcv"] },
  kia: { ceed: ["ceedsw"] },
  nissan: { qashqai: ["qashqai2"] },
  mg: { s9: ["mgs9"], mgs9: ["s9"] },
};

/** Exact normalized model match, plus the brand's explicit inclusion list. */
export function modelMatches(make: string, listingModel: string, pageModel: string): boolean {
  const car = normModel(listingModel);
  const page = normModel(pageModel);
  if (!car || !page) return false;
  if (car === page) return true;
  const brand = normModel(make).startsWith("mercedes") ? "mercedesbenz" : normModel(make);
  return (MODEL_INCLUSIONS[brand]?.[page] ?? []).includes(car);
}

/** The exact /cars/{make}/{model}/{fuel} match rule, shared by the page and the SSR loaders. */
export function filterMakeModel<T extends MakeModelCar>(cars: T[], { make, model, fuel }: MakeModelParams): T[] {
  if (!make) return cars;
  const detectedCity = detectCity(model);
  const norm = (s: string) => s.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]/g, "");
  return cars.filter((car) => {
    const carMake = norm(car.make || "");
    const urlMake = norm(make);
    if (!(carMake === urlMake || (urlMake === "mercedes" && carMake.includes("mercedes")))) return false;
    if (detectedCity) return normalizeCity(car.location || "").includes(normalizeCity(detectedCity));
    if (model) {
      const matchesModel = modelMatches(urlMake, car.model || "", model);
      const modelIsFuel = isKnownFuelSlug(model) && matchesFuel(car.fuelCode, model);
      if (!matchesModel && !modelIsFuel) return false;
    }
    if (fuel && !matchesFuel(car.fuelCode, fuel)) return false;
    return true;
  });
}

type Row = Record<string, any>;

/** Map a public listing row to the fields the match rule needs (same as MakeModelPage). */
export const toMakeModelCar = (d: Row): MakeModelCar => ({
  id: d.id,
  make: d.make || undefined,
  model: d.model || undefined,
  fuel: (d.attributes && d.attributes.fuel_type) || undefined,
  fuelCode: d.fuel_type ?? null,
  location: [d.location_city, d.location_region, d.location_country].filter(Boolean).join(", "),
});

/** Number of listings MakeModelPage shows for these params (RO, passenger cars). */
export function countMakeModel(rows: Row[], params: MakeModelParams): number {
  const eligible = rows.filter((r) => isEligibleCityListing(r as never));
  const page = !params.fuel ? getModelPage(params.make, params.model) : null;
  if (page) return eligible.filter((r) => matchesModelPage(page, r as never)).length;
  return filterMakeModel(eligible.map(toMakeModelCar), params).length;
}

export const MAKE_MODEL_NOINDEX = "noindex, follow";
/** Robots for a brand/model/fuel page: noindex only when the count is known to be 0. */
export const makeModelRobots = (count: number | null | undefined) => (count === 0 ? MAKE_MODEL_NOINDEX : null);

/** /cars/{make}/{model}/{fuel}: indexable only from this many active listings (same as cities). */
export const FUEL_PAGE_MIN_LISTINGS = 3;
/** Robots for a brand/model/fuel page: noindex below the threshold or when the count is unknown. */
export const fuelPageRobots = (count: number | null | undefined) =>
  count == null || count < FUEL_PAGE_MIN_LISTINGS ? MAKE_MODEL_NOINDEX : null;
export const fuelPageInSitemap = (count: number | null | undefined) => fuelPageRobots(count) === null;

/** Canonical fuel slugs that get /cars/{make}/{model}/{fuel} sitemap entries. */
export const FUEL_PAGE_SLUGS = ["diesel", "benzina", "hibrid", "electric", "gpl"] as const;

/** Every /cars/{make}/{model}/{fuel} path with its count, using the page's own match rule. */
export function fuelPageCounts(rows: Row[]): Array<{ path: string; count: number; canonical: string }> {
  const combos = new Set<string>();
  for (const r of rows) {
    if (!isEligibleCityListing(r as never)) continue;
    const mk = slugify(r.make), md = slugify(r.model);
    if (mk && md) combos.add(`${mk}/${md}`);
  }
  const out: Array<{ path: string; count: number; canonical: string }> = [];
  for (const combo of [...combos].sort()) {
    const [make, model] = combo.split("/");
    for (const fuel of FUEL_PAGE_SLUGS) {
      const count = countMakeModel(rows, { make, model, fuel });
      const path = `/cars/${combo}/${fuel}`;
      // Parent lookup only matters for pages that could enter the sitemap.
      const canonical = fuelPageInSitemap(count) ? fuelPageCanonicalPath(rows, { make, model, fuel }) : path;
      out.push({ path, count, canonical });
    }
  }
  return out;
}

/** Sorted ids a /cars/{make}/{model}/{fuel} page shows (same match rule as the page). */
function fuelPageIdKey(eligible: Row[], params: MakeModelParams): string {
  return filterMakeModel(eligible.map(toMakeModelCar), params).map((c) => c.id).sort().join(",");
}

/**
 * Parent model slug for a brand/model/fuel page: the shortest existing model slug of the
 * same brand that is a hyphen prefix of this one (seria-5 for seria-5-e61) and shows the
 * exact same listing ids for the same fuel. Null when none → the page keeps its own canonical.
 */
export function fuelPageParentModel(rows: Row[], { make, model, fuel }: MakeModelParams): string | null {
  if (!make || !model || !fuel) return null;
  const eligible = rows.filter((r) => isEligibleCityListing(r as never));
  const makeSlug = slugify(make);
  const candidates = new Set<string>();
  for (const r of eligible) {
    const md = slugify(r.model);
    if (slugify(r.make) === makeSlug && md && md !== model && model.startsWith(`${md}-`)) candidates.add(md);
  }
  if (candidates.size === 0) return null;
  const own = fuelPageIdKey(eligible, { make, model, fuel });
  if (!own) return null;
  const sorted = [...candidates].sort((a, b) => a.length - b.length);
  return sorted.find((md) => fuelPageIdKey(eligible, { make, model: md, fuel }) === own) ?? null;
}

/** Canonical path of a brand/model/fuel page: the parent page when the sets are identical, else itself. */
export function fuelPageCanonicalPath(rows: Row[], params: MakeModelParams): string {
  const parent = fuelPageParentModel(rows, params);
  return `/cars/${params.make}/${parent ?? params.model}/${params.fuel}`;
}
